import { useEffect, useMemo, useState } from 'react'
import { useDebounce } from '../../hooks/useDebounce'
import { Link, useLocation } from 'react-router-dom'
import { DENOMINATION_OPTIONS, churchNamesLikelyMatch } from '@fellowship/shared'
import { getEventType } from '../../components/ui/eventTypes'
import {
  useAdminAllChurches,
  useAdminCreateChurch,
  useAdminUpdateChurch,
  useAdminDeleteChurch,
  useAdminRestoreChurch,
  useAdminChurchDetail,
  useAdminAddChurchMember,
  useAdminRemoveChurchMember,
  useAdminUpdateChurchMember,
  useAdminScrapedChurches,
  useAdminChurchOutreach,
  useAdminEnrichChurch,
  useAdminAutoScrape,
  useAdminChurchDuplicates,
  useAdminDismissDuplicatePair,
} from '../../hooks/useAdmin'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { AdminModal, adminFieldClass } from '../../components/admin/AdminModal'
import { DataTable } from '../../components/admin/DataTable'
import { BulletinImportSection } from '../../components/admin/BulletinImportSection'
import { Avatar } from '../../components/ui/Avatar'
import AdminDiscoverPage from './AdminDiscoverPage'
import type { ColumnDef } from '@tanstack/react-table'
import type { AdminChurch, AdminChurchDetail, ChurchDuplicateCandidate, ChurchDuplicatePair } from '../../types/admin'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const hours = Math.floor(ms / 3_600_000)
  if (hours < 1) return '<1h'
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

function churchConfidence(c: AdminChurch): number {
  return (
    (c.name ? 1 : 0) +
    (c.addressLine1 ? 1 : 0) +
    (c.phone ? 1 : 0) +
    (c.email ? 1 : 0) +
    (c.websiteUrl ? 1 : 0)
  )
}

function churchRisk(c: AdminChurch): string | null {
  if (!c.addressLine1 && !c.city) return 'No address found'
  if (!c.phone && !c.email) return 'No contact info'
  return null
}

function computeDupeIds(churches: AdminChurch[]): Set<string> {
  const dupeIds = new Set<string>()
  for (let i = 0; i < churches.length; i++) {
    for (let j = i + 1; j < churches.length; j++) {
      if (churchNamesLikelyMatch(churches[i].name, churches[j].name)) {
        dupeIds.add(churches[i].id)
        dupeIds.add(churches[j].id)
      }
    }
  }
  return dupeIds
}

// ─── Duplicates tab ───────────────────────────────────────────────────────────

function formatDupeDistance(meters: number): string {
  const feet = meters * 3.28084
  if (feet < 1000) return `${Math.round(feet)} ft apart`
  return `${(meters / 1609.34).toFixed(2)} mi apart`
}

const SUBSCRIPTION_LABELS: Record<string, string> = {
  active: 'Active subscription',
  trialing: 'Trialing',
  past_due: 'Past due',
  canceled: 'Canceled subscription',
}

function DuplicateCandidateColumn({
  candidate,
  onArchive,
  archiving,
}: {
  candidate: ChurchDuplicateCandidate
  onArchive: () => void
  archiving: boolean
}) {
  const hasLiveBilling = candidate.subscriptionStatus != null && candidate.subscriptionStatus !== 'canceled'
  return (
    <div className="flex-1 min-w-0 rounded-xl border border-line-soft p-4">
      <Link
        to={`/churches/${candidate.slug}`}
        target="_blank"
        rel="noreferrer"
        className="text-[15px] font-semibold text-ink no-underline hover:underline"
      >
        {candidate.name}
      </Link>
      <div className="text-[13px] text-ink-2 mt-1">
        {[candidate.addressLine1, candidate.city, candidate.state].filter(Boolean).join(', ') || 'No address on file'}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${candidate.claimed ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
          {candidate.claimed ? 'Claimed' : 'Unclaimed'}
        </span>
        {candidate.verified && (
          <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Verified</span>
        )}
        {candidate.approvalStatus !== 'approved' && (
          <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{candidate.approvalStatus}</span>
        )}
        {hasLiveBilling && (
          <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
            {SUBSCRIPTION_LABELS[candidate.subscriptionStatus!] ?? candidate.subscriptionStatus}
          </span>
        )}
      </div>
      <div className="text-[12px] text-muted mt-2.5">
        {candidate.eventCount} event{candidate.eventCount === 1 ? '' : 's'} · {candidate.adminCount} admin{candidate.adminCount === 1 ? '' : 's'} · created {formatDate(candidate.createdAt)}
      </div>
      <button
        type="button"
        onClick={onArchive}
        disabled={archiving || hasLiveBilling}
        title={hasLiveBilling ? 'Has a live subscription — resolve billing before archiving' : undefined}
        className="mt-3 h-8 px-3 rounded-lg border border-line-soft bg-surface text-[12px] font-semibold text-danger cursor-pointer font-sans disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Archive this one
      </button>
    </div>
  )
}

function DuplicatePairCard({
  pair,
  onArchive,
  onDismiss,
  archivingId,
  dismissing,
}: {
  pair: ChurchDuplicatePair
  onArchive: (candidate: ChurchDuplicateCandidate) => void
  onDismiss: () => void
  archivingId: string | null
  dismissing: boolean
}) {
  return (
    <div className="rounded-2xl border border-line-soft p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] font-semibold text-muted">{formatDupeDistance(pair.distanceMeters)}</span>
        <button
          type="button"
          onClick={onDismiss}
          disabled={dismissing}
          className="h-7 px-3 rounded-lg border border-line-soft bg-surface text-[12px] font-semibold text-ink-2 cursor-pointer font-sans disabled:opacity-50"
        >
          {dismissing ? 'Dismissing…' : 'Not a duplicate'}
        </button>
      </div>
      <div className="flex gap-3 flex-col sm:flex-row">
        <DuplicateCandidateColumn
          candidate={pair.churchA}
          onArchive={() => onArchive(pair.churchA)}
          archiving={archivingId === pair.churchA.id}
        />
        <DuplicateCandidateColumn
          candidate={pair.churchB}
          onArchive={() => onArchive(pair.churchB)}
          archiving={archivingId === pair.churchB.id}
        />
      </div>
    </div>
  )
}

function ConfBars({ score }: { score: number }) {
  const color = score >= 4 ? '#22c55e' : score >= 3 ? '#f59e0b' : '#ef4444'
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-0.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{ width: 5, height: 14, borderRadius: 1.5, background: i < score ? color : 'var(--line-soft)' }}
          />
        ))}
      </div>
      <span className="text-[11px] text-muted font-mono">{score}/5</span>
    </div>
  )
}

// ─── Church form state ────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  coordinator: 'Coordinator',
  viewer: 'Viewer',
}

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  owner: { bg: '#FFF3CD', color: '#856404' },
  coordinator: { bg: '#D1ECF1', color: '#0C5460' },
  viewer: { bg: 'var(--surface-2)', color: 'var(--muted)' },
}

const stripeBase = 'https://dashboard.stripe.com'

type ChurchFormState = {
  name: string; slug: string; addressLine1: string; city: string; state: string
  zip: string; locationLat: string; locationLng: string; denomination: string
  phone: string; email: string; websiteUrl: string; verified: boolean
  claimed: boolean; ownerUserId: string
}

const emptyChurchForm = (): ChurchFormState => ({
  name: '', slug: '', addressLine1: '', city: '', state: '', zip: '',
  locationLat: '45.52', locationLng: '-122.68', denomination: 'seventh-day-adventist',
  phone: '', email: '', websiteUrl: '', verified: false, claimed: true, ownerUserId: '',
})

function churchToForm(c: AdminChurch | AdminChurchDetail): ChurchFormState {
  return {
    name: c.name, slug: c.slug, addressLine1: c.addressLine1 ?? '',
    city: c.city ?? '', state: c.state ?? '', zip: '',
    locationLat: String(c.locationLat), locationLng: String(c.locationLng),
    denomination: c.denomination, phone: c.phone ?? '', email: c.email ?? '',
    websiteUrl: c.websiteUrl ?? '', verified: c.verified, claimed: c.claimed, ownerUserId: '',
  }
}

// ─── Church detail modal ──────────────────────────────────────────────────────

function ChurchDetailModal({ churchId, onClose }: { churchId: string; onClose: () => void }) {
  const { data, isLoading } = useAdminChurchDetail(churchId)
  const updateChurch = useAdminUpdateChurch()
  const updateMember = useAdminUpdateChurchMember()
  const addMember = useAdminAddChurchMember()
  const removeMember = useAdminRemoveChurchMember()
  const enrichChurch = useAdminEnrichChurch()
  const autoScrape = useAdminAutoScrape()
  const church = data?.data

  const [form, setForm] = useState<ChurchFormState>(emptyChurchForm())
  const [newMemberUserId, setNewMemberUserId] = useState('')
  const [newMemberRole, setNewMemberRole] = useState('coordinator')
  const [scrapeResult, setScrapeResult] = useState<{ created: number; updated: number; foundUrl: string } | null>(null)
  const [scrapeError, setScrapeError] = useState<string | null>(null)

  useEffect(() => { if (church) setForm(churchToForm(church)) }, [church])
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function handleSaveChurch() {
    updateChurch.mutate({
      id: churchId, name: form.name.trim(), slug: form.slug.trim() || undefined,
      addressLine1: form.addressLine1 || undefined, city: form.city || undefined,
      state: form.state || undefined, zip: form.zip || undefined,
      locationLat: parseFloat(form.locationLat), locationLng: parseFloat(form.locationLng),
      denomination: form.denomination, phone: form.phone || null, email: form.email || null,
      websiteUrl: form.websiteUrl || undefined, verified: form.verified, claimed: form.claimed,
    })
  }

  function handleAddMember() {
    if (!newMemberUserId.trim()) return
    addMember.mutate({ churchId, userId: newMemberUserId.trim(), role: newMemberRole }, {
      onSuccess: () => setNewMemberUserId(''),
    })
  }

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '32px 40px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--bg)', borderRadius: 16, width: '100%', maxWidth: 1100, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.35)', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 24px', borderBottom: '1px solid var(--line-soft)', flexShrink: 0 }}>
          <h2 style={{ flex: 1, margin: 0, fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--ink)' }}>
            {isLoading ? 'Loading…' : (church?.name ?? 'Church')}
          </h2>
          {church && <span style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'ui-monospace,"SF Mono",Menlo,monospace' }}>{church.id}</span>}
          {church && (church.websiteUrl || church.sourceUrl) && (
            <button
              type="button"
              onClick={() => {
                setScrapeResult(null)
                setScrapeError(null)
                autoScrape.mutate({ churchId: church.id }, {
                  onSuccess: (res) => setScrapeResult({ created: res.data.eventsCreated, updated: res.data.eventsSkipped, foundUrl: res.data.foundUrl }),
                  onError: (err) => setScrapeError(err instanceof Error ? err.message : 'Scrape failed'),
                })
              }}
              disabled={autoScrape.isPending}
              title="Find and import events from this church's website"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 8,
                border: '1px solid var(--line-soft)',
                background: autoScrape.isPending ? 'linear-gradient(135deg,#16a34a18,#15803d18)' : 'var(--surface)',
                color: autoScrape.isPending ? '#16a34a' : 'var(--ink-2)',
                cursor: autoScrape.isPending ? 'default' : 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                flexShrink: 0,
              }}
            >
              {autoScrape.isPending ? '⟳ Finding events…' : '⟳ Find Events'}
            </button>
          )}
          {church && (
            <button
              type="button"
              onClick={() => enrichChurch.mutate(church.id)}
              disabled={enrichChurch.isPending}
              title="AI enrich: fill missing phone, email, city, state, denomination"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 8,
                border: '1px solid var(--line-soft)',
                background: enrichChurch.isPending ? 'linear-gradient(135deg,#7c3aed18,#4f46e518)' : 'var(--surface)',
                color: enrichChurch.isPending ? 'var(--primary)' : 'var(--ink-2)',
                cursor: enrichChurch.isPending ? 'default' : 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                flexShrink: 0,
              }}
            >
              {enrichChurch.isPending ? '✦ Enriching…' : '✦ Enrich'}
            </button>
          )}
          <button type="button" onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', cursor: 'pointer', fontSize: 16, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
        </div>

        {scrapeResult && (
          <div style={{ padding: '10px 24px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', fontSize: 13, color: '#15803d', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>✓ Found events from <a href={scrapeResult.foundUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#15803d', fontWeight: 600 }}>{scrapeResult.foundUrl}</a> — {scrapeResult.created} created, {scrapeResult.updated} skipped</span>
            <button type="button" onClick={() => setScrapeResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#15803d', fontSize: 16, lineHeight: 1 }}>✕</button>
          </div>
        )}
        {scrapeError && (
          <div style={{ padding: '10px 24px', background: '#fef2f2', borderBottom: '1px solid #fecaca', fontSize: 13, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>✗ {scrapeError}</span>
            <button type="button" onClick={() => setScrapeError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, lineHeight: 1 }}>✕</button>
          </div>
        )}
        {church && (
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div className="flex flex-col gap-3">
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Name<input className={adminFieldClass()} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></label>
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Slug<input className={adminFieldClass()} value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} /></label>
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Address<input className={adminFieldClass()} value={form.addressLine1} onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))} /></label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">City<input className={adminFieldClass()} value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} /></label>
                  <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">State<input className={adminFieldClass()} value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} /></label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Lat<input className={adminFieldClass()} value={form.locationLat} onChange={(e) => setForm((f) => ({ ...f, locationLat: e.target.value }))} /></label>
                  <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Lng<input className={adminFieldClass()} value={form.locationLng} onChange={(e) => setForm((f) => ({ ...f, locationLng: e.target.value }))} /></label>
                </div>
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
                  Denomination
                  <select className={adminFieldClass()} value={form.denomination} onChange={(e) => setForm((f) => ({ ...f, denomination: e.target.value }))}>
                    {DENOMINATION_OPTIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                    {form.denomination && !DENOMINATION_OPTIONS.some((d) => d.key === form.denomination) && (
                      <option value={form.denomination}>{form.denomination}</option>
                    )}
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Phone<input className={adminFieldClass()} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></label>
                  <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Email<input className={adminFieldClass()} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></label>
                </div>
                <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Website<input className={adminFieldClass()} value={form.websiteUrl} onChange={(e) => setForm((f) => ({ ...f, websiteUrl: e.target.value }))} /></label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                    <input type="checkbox" checked={form.verified} onChange={(e) => setForm((f) => ({ ...f, verified: e.target.checked }))} />
                    Verified
                  </label>
                  <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                    <input type="checkbox" checked={form.claimed} onChange={(e) => setForm((f) => ({ ...f, claimed: e.target.checked }))} />
                    Claimed
                  </label>
                </div>
                <button type="button" onClick={handleSaveChurch} disabled={updateChurch.isPending} className="self-start px-4 py-2 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50">
                  {updateChurch.isPending ? 'Saving…' : 'Save church'}
                </button>
              </div>
              <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 20 }}>
                <BulletinImportSection churchId={church.id} churchName={church.name} />
              </div>
            </div>

            <div style={{ width: 340, flexShrink: 0, borderLeft: '1px solid var(--line-soft)', overflowY: 'auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 20, background: 'var(--surface)' }}>
              <div>
                <div className="text-[12px] font-semibold text-ink-2 mb-2.5">Events ({church.events.length})</div>
                {church.events.length === 0 ? (
                  <div className="text-[13px] text-muted">No events linked to this church.</div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {church.events.map((ev) => {
                      const t = getEventType(ev.eventType as Parameters<typeof getEventType>[0])
                      return (
                        <Link key={ev.id} to={`/events/${ev.id}`} className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-[10px] bg-s2 border border-line-soft no-underline text-inherit hover:border-primary/40">
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold text-primary truncate">{ev.title}</div>
                            <div className="text-[10px] text-muted font-mono mt-0.5 truncate">{ev.id}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold" style={{ background: t.soft, color: t.color }}>{t.label}</span>
                            <span className="text-[10px] text-muted">{ev.status} · {ev.source}</span>
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>

              {(church.stripeCustomerId || church.stripeSubscriptionId) && (
                <div>
                  <div className="text-[12px] font-semibold text-ink-2 mb-2">Stripe</div>
                  <div className="flex gap-2 flex-wrap">
                    {church.stripeCustomerId && <a href={`${stripeBase}/customers/${church.stripeCustomerId}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 rounded-md text-[12px] font-semibold no-underline" style={{ background: '#EEF0FF', color: '#635bff', border: '1px solid #C7CAFE' }}>Customer ↗</a>}
                    {church.stripeSubscriptionId && <a href={`${stripeBase}/subscriptions/${church.stripeSubscriptionId}`} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 rounded-md text-[12px] font-semibold no-underline" style={{ background: '#EEF0FF', color: '#635bff', border: '1px solid #C7CAFE' }}>Subscription ↗</a>}
                  </div>
                </div>
              )}

              <div>
                <div className="text-[12px] font-semibold text-ink-2 mb-2">Add staff member</div>
                <div className="flex gap-2">
                  <input className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-line-soft bg-bg text-[12px] font-mono box-border" placeholder="User UUID" value={newMemberUserId} onChange={(e) => setNewMemberUserId(e.target.value)} />
                  <select className="w-[110px] shrink-0 px-2 py-2 rounded-lg border border-line-soft bg-bg text-[13px] font-sans box-border" value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value)}>
                    <option value="owner">Owner</option>
                    <option value="coordinator">Coordinator</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button type="button" onClick={handleAddMember} disabled={addMember.isPending} className="px-3 py-2 rounded-lg border-0 bg-primary text-on-primary text-[12px] font-semibold cursor-pointer font-sans shrink-0">Add</button>
                </div>
              </div>

              <div>
                <div className="text-[12px] font-semibold text-ink-2 mb-2.5">Staff &amp; admins ({church.admins.length})</div>
                {church.admins.length === 0 ? (
                  <div className="text-[13px] text-muted">No admins on record.</div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {church.admins.map((admin) => {
                      const roleStyle = ROLE_COLORS[admin.role] ?? ROLE_COLORS.viewer
                      return (
                        <div key={admin.userId} className="flex items-center justify-between px-3.5 py-2.5 rounded-[10px] bg-s2 border border-line-soft gap-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Avatar name={admin.displayName} src={admin.avatarUrl} size={32} />
                            <div className="min-w-0">
                              <div className="text-[13px] font-semibold text-ink">{admin.displayName}</div>
                              <div className="text-[11px] text-muted mt-px truncate">{admin.email}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold" style={{ background: roleStyle.bg, color: roleStyle.color }}>{ROLE_LABELS[admin.role] ?? admin.role}</span>
                            <select value={admin.role} aria-label={`Change role for ${admin.displayName}`} onChange={(e) => updateMember.mutate({ churchId: church.id, userId: admin.userId, role: e.target.value })} className="text-[12px] px-1.5 py-0.5 rounded-md border border-line bg-surface text-ink cursor-pointer font-sans">
                              <option value="owner">Owner</option>
                              <option value="coordinator">Coordinator</option>
                              <option value="viewer">Viewer</option>
                            </select>
                            <button type="button" onClick={() => removeMember.mutate({ churchId: church.id, userId: admin.userId })} className="text-[11px] text-danger-deep cursor-pointer bg-transparent border-0 font-sans">Remove</button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Live tab columns ─────────────────────────────────────────────────────────

function liveChurchColumns(
  onEdit: (id: string) => void,
  onDelete: (c: AdminChurch) => void,
  onRestore: (id: string) => void,
  restorePending: boolean,
): ColumnDef<AdminChurch, unknown>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      meta: { label: 'Name' },
      cell: ({ row }) => (
        <Link to={`/churches/${row.original.slug}`} className="no-underline block max-w-[240px]">
          <div className="text-primary font-semibold text-[13px] hover:underline">{row.original.name}</div>
          <div className="text-[11px] text-muted mt-0.5">/{row.original.slug}</div>
        </Link>
      ),
    },
    {
      id: 'location',
      header: 'Location',
      meta: { label: 'Location' },
      accessorFn: (row) => [row.city, row.state].filter(Boolean).join(', ') || null,
      cell: ({ getValue }) => <span className="text-ink-2">{(getValue() as string | null) ?? '—'}</span>,
    },
    {
      accessorKey: 'denomination',
      header: 'Denomination',
      meta: { label: 'Denomination' },
      cell: ({ getValue }) => <span className="text-ink-2">{String(getValue())}</span>,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      meta: { label: 'Email' },
      cell: ({ getValue }) => {
        const v = getValue() as string | null
        return v ? <span className="text-ink-2 text-[12px]">{v}</span> : <span className="text-muted text-[12px]">—</span>
      },
    },
    {
      accessorKey: 'subscriptionPlan',
      header: 'Plan',
      meta: { label: 'Plan' },
      cell: ({ getValue }) => {
        const v = getValue() as string | null
        return <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${v ? 'bg-primary-soft text-primary' : 'bg-s2 text-muted'}`}>{v ?? 'free'}</span>
      },
    },
    {
      id: 'status',
      header: 'Status',
      meta: { label: 'Status' },
      accessorFn: (row) => (row.verified ? 'verified' : row.claimed ? 'claimed' : 'unclaimed'),
      cell: ({ row }) => (
        <span className="text-[12px]">
          {row.original.verified && <span className="text-[#1e7e34]">✓ Verified</span>}
          {!row.original.verified && !row.original.claimed && <span className="text-muted">Unclaimed</span>}
          {!row.original.verified && row.original.claimed && <span className="text-ink-2">Claimed</span>}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Joined',
      meta: { label: 'Joined' },
      cell: ({ getValue }) => <span className="text-muted">{formatDate(String(getValue()))}</span>,
    },
    {
      id: 'actions',
      header: '',
      meta: { skipExport: true },
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const c = row.original
        return (
          <div className="flex gap-2">
            {c.deletedAt ? (
              <button type="button" onClick={() => onRestore(c.id)} disabled={restorePending} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-good bg-good-soft text-good-deep disabled:opacity-50">Restore</button>
            ) : (
              <>
                <button type="button" onClick={() => onEdit(c.id)} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-line-soft bg-s2 text-ink">Edit</button>
                <button type="button" onClick={() => onDelete(c)} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-danger-line bg-danger-soft text-danger-deep">Delete</button>
              </>
            )}
          </div>
        )
      },
    },
  ]
}

// ─── Review tab columns ───────────────────────────────────────────────────────

function reviewChurchColumns(
  dupeIds: Set<string>,
  onVerify: (id: string) => void,
  onDismiss: (id: string) => void,
  onOutreach: (id: string) => void,
  onEdit: (id: string) => void,
  outreachSentIds: Set<string>,
  verifyingId: string | null,
  dismissingId: string | null,
  outreachingId: string | null,
): ColumnDef<AdminChurch, unknown>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      meta: { label: 'Name' },
      cell: ({ row }) => {
        const c = row.original
        return (
          <div>
            <button type="button" onClick={() => onEdit(c.id)} className="text-primary font-semibold text-[13px] hover:underline border-0 bg-transparent cursor-pointer p-0 font-sans text-left">
              {c.name}
            </button>
            {c.sourceUrl && <div className="text-[11px] text-muted truncate max-w-[200px]">{c.sourceUrl}</div>}
          </div>
        )
      },
    },
    {
      id: 'location',
      header: 'Location',
      meta: { label: 'Location' },
      accessorFn: (row) => [row.city, row.state].filter(Boolean).join(', ') || null,
      cell: ({ getValue }) => <span className="text-ink-2 text-[12px]">{(getValue() as string | null) ?? '—'}</span>,
    },
    {
      accessorKey: 'denomination',
      header: 'Denomination',
      meta: { label: 'Denomination' },
      cell: ({ getValue }) => <span className="text-ink-2 text-[12px]">{String(getValue()) || '—'}</span>,
    },
    {
      id: 'contact',
      header: 'Contact',
      meta: { label: 'Contact' },
      accessorFn: (row) => row.email ?? row.phone ?? null,
      cell: ({ row }) => {
        const c = row.original
        if (!c.email && !c.phone) return <span className="text-muted text-[12px]">—</span>
        return (
          <div className="text-[12px]">
            {c.email && <div className="text-ink-2">{c.email}</div>}
            {c.phone && <div className="text-muted">{c.phone}</div>}
          </div>
        )
      },
    },
    {
      id: 'confidence',
      header: 'Confidence',
      meta: { label: 'Confidence' },
      accessorFn: (row) => churchConfidence(row),
      cell: ({ getValue }) => <ConfBars score={getValue() as number} />,
    },
    {
      id: 'age',
      header: 'Age',
      meta: { label: 'Age' },
      accessorFn: (row) => Date.now() - new Date(row.createdAt).getTime(),
      cell: ({ row }) => <span className="text-muted text-[12px] font-mono">{formatAge(row.original.createdAt)}</span>,
    },
    {
      id: 'flags',
      header: 'Flags',
      meta: { label: 'Flags' },
      enableSorting: false,
      accessorFn: (row) => {
        const risk = churchRisk(row)
        const dupe = dupeIds.has(row.id)
        return [risk ?? '', dupe ? 'dupe' : ''].filter(Boolean).join(',')
      },
      cell: ({ row }) => {
        const risk = churchRisk(row.original)
        const dupe = dupeIds.has(row.original.id)
        if (!risk && !dupe) return <span className="text-muted text-[12px]">—</span>
        return (
          <div className="flex flex-col gap-1">
            {risk && <span className="text-amber-700 text-[11px] font-medium">⚠ {risk}</span>}
            {dupe && <span className="text-primary text-[11px] font-medium">⚠ Possible duplicate</span>}
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      meta: { skipExport: true },
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const c = row.original
        return (
          <div className="flex gap-2 flex-wrap justify-end">
            <button type="button" onClick={() => onVerify(c.id)} disabled={verifyingId === c.id} className="px-3 py-1.5 rounded-md text-[12px] font-semibold cursor-pointer border-0 bg-primary text-on-primary font-sans disabled:opacity-50">
              {verifyingId === c.id ? '…' : '✓ Verify'}
            </button>
            <button type="button" onClick={() => onEdit(c.id)} className="px-3 py-1.5 rounded-md text-[12px] cursor-pointer font-sans border border-line-soft bg-s2 text-ink">Edit</button>
            {c.email && (
              outreachSentIds.has(c.id)
                ? <span className="px-3 py-1.5 text-[11px] text-good-deep font-semibold self-center">✓ Sent</span>
                : <button type="button" onClick={() => onOutreach(c.id)} disabled={outreachingId === c.id} title="Send outreach email" className="px-3 py-1.5 rounded-md text-[12px] cursor-pointer font-sans border border-line-soft bg-s2 text-ink disabled:opacity-50">
                    {outreachingId === c.id ? '…' : '📨'}
                  </button>
            )}
            <button type="button" onClick={() => onDismiss(c.id)} disabled={dismissingId === c.id} className="px-3 py-1.5 rounded-md text-[12px] cursor-pointer font-sans border border-danger-line bg-danger-soft text-danger-deep disabled:opacity-50">
              {dismissingId === c.id ? '…' : '✗'}
            </button>
          </div>
        )
      },
    },
  ]
}

// ─── Tab bar ──────────────────────────────────────────────────────────────────

type ChurchTab = 'live' | 'review' | 'duplicates' | 'discover'

function TabBar({
  tab,
  onChange,
  reviewCount,
  duplicatesCount,
}: {
  tab: ChurchTab
  onChange: (t: ChurchTab) => void
  reviewCount: number
  duplicatesCount: number
}) {
  const tabs: { id: ChurchTab; label: string; count?: number }[] = [
    { id: 'live', label: 'Live' },
    { id: 'review', label: 'Review', count: reviewCount },
    { id: 'duplicates', label: 'Duplicates', count: duplicatesCount },
    { id: 'discover', label: 'Discover' },
  ]
  return (
    <div className="flex gap-1 mb-5 border-b border-line-soft pb-0">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={`px-4 py-2 text-[13px] font-semibold rounded-t-lg border-b-2 cursor-pointer font-sans transition-colors -mb-px ${
            tab === t.id
              ? 'border-primary text-primary bg-transparent'
              : 'border-transparent text-ink-2 hover:text-ink bg-transparent'
          }`}
          style={{ border: 'none', borderBottom: tab === t.id ? '2px solid var(--primary, #3B4CCA)' : '2px solid transparent' }}
        >
          {t.label}
          {t.count != null && t.count > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">{t.count}</span>
          )}
        </button>
      ))}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

type DiscoverNavState = { discoverLat?: number; discoverLng?: number; discoverRadius?: number } | null

export default function AdminChurchesPage() {
  const location = useLocation()
  const discoverInit = (location.state as DiscoverNavState) ?? null

  const [tab, setTab] = useState<ChurchTab>(discoverInit?.discoverLat != null ? 'discover' : 'live')

  // Live tab state
  const [confirmDelete, setConfirmDelete] = useState<AdminChurch | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState<ChurchFormState>(emptyChurchForm())
  const [showDeleted, setShowDeleted] = useState(false)
  const [churchSearch, setChurchSearch] = useState('')
  const debouncedChurchSearch = useDebounce(churchSearch.trim(), 300)
  const [locationFilter, setLocationFilter] = useState('')
  const debouncedLocation = useDebounce(locationFilter.trim(), 300)
  const [emailFilter, setEmailFilter] = useState<'' | 'yes' | 'no'>('')
  const [denomFilter, setDenomFilter] = useState('')

  // Live tab bulk enrich state
  const [liveBatchEnriching, setLiveBatchEnriching] = useState(false)
  const [liveBatchProgress, setLiveBatchProgress] = useState<{ done: number; total: number } | null>(null)

  // Review tab state
  const [verifyingId, setVerifyingId] = useState<string | null>(null)
  const [reviewDismissingId, setReviewDismissingId] = useState<string | null>(null)
  const [outreachingId, setOutreachingId] = useState<string | null>(null)
  const [outreachSentIds, setOutreachSentIds] = useState<Set<string>>(new Set())
  const [batchEnriching, setBatchEnriching] = useState(false)
  const [enrichProgress, setEnrichProgress] = useState<{ done: number; total: number } | null>(null)

  // Duplicates tab state
  const [archiveDuplicateTarget, setArchiveDuplicateTarget] = useState<ChurchDuplicateCandidate | null>(null)
  const [dismissingPairKey, setDismissingPairKey] = useState<string | null>(null)

  // Queries
  const { data: allChurchData, isLoading: allLoading } = useAdminAllChurches({
    showDeleted,
    q: debouncedChurchSearch || undefined,
    location: debouncedLocation || undefined,
    hasEmail: emailFilter || undefined,
    denomination: denomFilter || undefined,
  })
  const { data: reviewData, isLoading: reviewLoading } = useAdminScrapedChurches()
  const { data: duplicatesData, isLoading: duplicatesLoading } = useAdminChurchDuplicates()

  const churches = allChurchData?.data ?? []
  const total = allChurchData?.meta.total ?? 0
  const reviewChurches = useMemo(() => reviewData?.data ?? [], [reviewData?.data])
  const duplicatePairs = useMemo(() => duplicatesData?.data ?? [], [duplicatesData?.data])

  // Mutations
  const createChurch = useAdminCreateChurch()
  const deleteChurch = useAdminDeleteChurch()
  const restoreChurch = useAdminRestoreChurch()
  const updateChurch = useAdminUpdateChurch()
  const churchOutreach = useAdminChurchOutreach()
  const enrichChurch = useAdminEnrichChurch()
  const dismissDuplicate = useAdminDismissDuplicatePair()

  // Computed
  const dupeIds = useMemo(() => computeDupeIds(reviewChurches), [reviewChurches])
  const autoSafeCount = useMemo(
    () => reviewChurches.filter((c) => churchConfidence(c) >= 5 && !churchRisk(c) && !dupeIds.has(c.id)).length,
    [reviewChurches, dupeIds],
  )

  // Handlers
  function handleVerify(id: string) {
    setVerifyingId(id)
    updateChurch.mutate({ id, verified: true }, {
      onSuccess: () => setVerifyingId(null),
      onError: () => setVerifyingId(null),
    })
  }

  function handleReviewDismiss(id: string) {
    setReviewDismissingId(id)
    deleteChurch.mutate(id, {
      onSuccess: () => setReviewDismissingId(null),
      onError: () => setReviewDismissingId(null),
    })
  }

  function handleOutreach(id: string) {
    setOutreachingId(id)
    churchOutreach.mutate(id, {
      onSuccess: () => {
        setOutreachingId(null)
        setOutreachSentIds((prev) => new Set([...prev, id]))
      },
      onError: () => setOutreachingId(null),
    })
  }

  function handleAutoApprove() {
    const safe = reviewChurches.filter((c) => churchConfidence(c) >= 5 && !churchRisk(c))
    for (const c of safe) handleVerify(c.id)
  }

  async function handleBatchEnrich() {
    const toEnrich = reviewChurches.filter(
      (c) => !c.phone || !c.email || !c.city || !c.state,
    )
    if (toEnrich.length === 0) return
    setBatchEnriching(true)
    setEnrichProgress({ done: 0, total: toEnrich.length })
    for (let i = 0; i < toEnrich.length; i++) {
      try {
        await enrichChurch.mutateAsync(toEnrich[i].id)
      } catch { /* skip failed individual enrichments */ }
      setEnrichProgress({ done: i + 1, total: toEnrich.length })
      // Nominatim rate limit: stagger calls
      if (i < toEnrich.length - 1) await new Promise((r) => setTimeout(r, 1200))
    }
    setBatchEnriching(false)
    setEnrichProgress(null)
  }

  async function handleLiveBatchEnrich() {
    const toEnrich = churches.filter((c) => !c.email)
    if (toEnrich.length === 0) return
    setLiveBatchEnriching(true)
    setLiveBatchProgress({ done: 0, total: toEnrich.length })
    for (let i = 0; i < toEnrich.length; i++) {
      try {
        await enrichChurch.mutateAsync(toEnrich[i].id)
      } catch { /* skip failures, continue batch */ }
      setLiveBatchProgress({ done: i + 1, total: toEnrich.length })
      if (i < toEnrich.length - 1) await new Promise((r) => setTimeout(r, 1200))
    }
    setLiveBatchEnriching(false)
    setLiveBatchProgress(null)
  }

  function handleLiveDelete(church: AdminChurch) {
    deleteChurch.mutate(church.id, {
      onSuccess: () => {
        setConfirmDelete(null)
        if (detailId === church.id) setDetailId(null)
      },
    })
  }

  function handleArchiveDuplicate(candidate: ChurchDuplicateCandidate) {
    deleteChurch.mutate(candidate.id, {
      onSuccess: () => setArchiveDuplicateTarget(null),
    })
  }

  function handleDismissDuplicate(pair: ChurchDuplicatePair) {
    setDismissingPairKey(`${pair.churchA.id}:${pair.churchB.id}`)
    dismissDuplicate.mutate(
      { churchIdA: pair.churchA.id, churchIdB: pair.churchB.id },
      { onSettled: () => setDismissingPairKey(null) },
    )
  }

  function handleCreate() {
    createChurch.mutate(
      {
        name: createForm.name.trim(),
        addressLine1: createForm.addressLine1,
        city: createForm.city,
        state: createForm.state,
        zip: createForm.zip || '00000',
        locationLat: parseFloat(createForm.locationLat),
        locationLng: parseFloat(createForm.locationLng),
        denomination: createForm.denomination,
        phone: createForm.phone || undefined,
        websiteUrl: createForm.websiteUrl || undefined,
        claimed: createForm.claimed,
        ownerUserId: createForm.ownerUserId.trim() || undefined,
      },
      { onSuccess: () => setCreating(false) },
    )
  }

  const liveCols = liveChurchColumns(
    (id) => setDetailId(id),
    (c) => setConfirmDelete(c),
    (id) => restoreChurch.mutate(id),
    restoreChurch.isPending,
  )

  const revCols = reviewChurchColumns(
    dupeIds, handleVerify, handleReviewDismiss, handleOutreach,
    (id) => setDetailId(id),
    outreachSentIds, verifyingId, reviewDismissingId, outreachingId,
  )

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="m-0 text-[24px] font-bold">Churches</h1>
          <div className="text-[13px] text-muted mt-1">
            {tab === 'live' && `${total} total`}
            {tab === 'review' && `${reviewChurches.length} pending review`}
            {tab === 'duplicates' && `${duplicatePairs.length} candidate pair${duplicatePairs.length === 1 ? '' : 's'}`}
            {tab === 'discover' && 'Scan OpenStreetMap or import a JSON file'}
          </div>
        </div>
        <div className="flex gap-2">
          {tab === 'live' && (
            <>
              <button type="button" onClick={() => setShowDeleted((v) => !v)} className={`h-9 px-3.5 rounded-lg border text-[13px] font-semibold cursor-pointer font-sans ${showDeleted ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-line-soft bg-surface text-ink-2'}`}>
                {showDeleted ? 'Hide deleted' : 'Show deleted'}
              </button>
              {!showDeleted && (
                <button type="button" onClick={() => { setCreateForm(emptyChurchForm()); setCreating(true) }} className="h-9 px-4 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans">
                  + Add church
                </button>
              )}
            </>
          )}
          {tab === 'review' && (
            <>
              <button
                type="button"
                onClick={() => void handleBatchEnrich()}
                disabled={batchEnriching || reviewChurches.length === 0}
                className="h-9 px-4 rounded-lg border border-line-soft text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff', border: 'none' }}
              >
                {enrichProgress
                  ? `✦ Enriching ${enrichProgress.done}/${enrichProgress.total}…`
                  : `✦ Enrich all (${reviewChurches.filter((c) => !c.phone || !c.email || !c.city || !c.state).length})`}
              </button>
              {autoSafeCount > 0 && (
                <button type="button" onClick={handleAutoApprove} className="h-9 px-4 rounded-lg border-0 bg-[#22c55e] text-white text-[13px] font-semibold cursor-pointer font-sans">
                  Auto-verify safe ({autoSafeCount})
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <TabBar tab={tab} onChange={setTab} reviewCount={reviewChurches.length} duplicatesCount={duplicatePairs.length} />

      {tab === 'live' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="search"
              placeholder="Search churches by name…"
              value={churchSearch}
              onChange={(e) => setChurchSearch(e.target.value)}
              style={{ flex: 1, maxWidth: 280, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 13 }}
            />
            <input
              type="search"
              placeholder="Location (city, state, zip)…"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              style={{ width: 200, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 13 }}
            />
            <select
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value as '' | 'yes' | 'no')}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 13 }}
            >
              <option value="">Email: any</option>
              <option value="yes">Has email</option>
              <option value="no">Missing email</option>
            </select>
            <select
              value={denomFilter}
              onChange={(e) => setDenomFilter(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 13 }}
            >
              <option value="">All denominations</option>
              {DENOMINATION_OPTIONS.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
            {(locationFilter || emailFilter || denomFilter) && (
              <button
                type="button"
                onClick={() => { setLocationFilter(''); setEmailFilter(''); setDenomFilter('') }}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-soft)', fontSize: 13, cursor: 'pointer' }}
              >
                Clear
              </button>
            )}
            {allChurchData && (
              <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                {allChurchData.meta.total.toLocaleString()} churches
              </span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              {liveBatchProgress && (
                <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 500 }}>
                  ✦ {liveBatchProgress.done}/{liveBatchProgress.total} enriched
                </span>
              )}
              {(() => {
                const missingEmail = churches.filter((c) => !c.email).length
                return missingEmail > 0 ? (
                  <button
                    type="button"
                    onClick={handleLiveBatchEnrich}
                    disabled={liveBatchEnriching}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px', borderRadius: 8,
                      border: '1px solid var(--line-soft)',
                      background: liveBatchEnriching ? 'linear-gradient(135deg,#7c3aed18,#4f46e518)' : 'var(--surface)',
                      color: liveBatchEnriching ? 'var(--primary)' : 'var(--ink-2)',
                      cursor: liveBatchEnriching ? 'default' : 'pointer',
                      fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                    }}
                  >
                    {liveBatchEnriching
                      ? '✦ Enriching…'
                      : `✦ Enrich ${missingEmail} missing email`}
                  </button>
                ) : null
              })()}
            </div>
          </div>
          <DataTable
            columns={liveCols}
            data={churches}
            isLoading={allLoading}
            sortable
            filterable
            configurable
            exportable
            exportFilename="fellowship-churches"
            pageSize={50}
            emptyMessage="No churches found."
          />
        </>
      )}

      {tab === 'review' && (
        <DataTable
          columns={revCols}
          data={reviewChurches}
          isLoading={reviewLoading}
          sortable
          filterable
          pageSize={50}
          emptyMessage="No churches pending review. Run the scraper or discover new churches to populate this list."
        />
      )}

      {tab === 'duplicates' && (
        <div>
          {duplicatesLoading && <p className="text-[13px] text-muted">Loading…</p>}
          {!duplicatesLoading && duplicatePairs.length === 0 && (
            <p className="text-[13px] text-muted">No candidate duplicates found — churches within ~1km with matching names show up here.</p>
          )}
          {duplicatePairs.map((pair) => (
            <DuplicatePairCard
              key={`${pair.churchA.id}:${pair.churchB.id}`}
              pair={pair}
              onArchive={setArchiveDuplicateTarget}
              onDismiss={() => handleDismissDuplicate(pair)}
              archivingId={deleteChurch.isPending ? archiveDuplicateTarget?.id ?? null : null}
              dismissing={dismissingPairKey === `${pair.churchA.id}:${pair.churchB.id}`}
            />
          ))}
        </div>
      )}

      {tab === 'discover' && (
        <AdminDiscoverPage
          hideTitle
          initialLat={discoverInit?.discoverLat}
          initialLng={discoverInit?.discoverLng}
          initialRadius={discoverInit?.discoverRadius}
        />
      )}

      {/* Shared modals */}
      {detailId && <ChurchDetailModal churchId={detailId} onClose={() => setDetailId(null)} />}

      {creating && (
        <AdminModal
          title="Add church"
          onClose={() => setCreating(false)}
          wide
          footer={
            <>
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-lg border border-line-soft bg-surface text-[13px] cursor-pointer font-sans">Cancel</button>
              <button type="button" onClick={handleCreate} disabled={createChurch.isPending || !createForm.name} className="px-4 py-2 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50">
                {createChurch.isPending ? 'Creating…' : 'Create'}
              </button>
            </>
          }
        >
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Name *<input className={adminFieldClass()} value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} /></label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Address line 1 *<input className={adminFieldClass()} value={createForm.addressLine1} onChange={(e) => setCreateForm((f) => ({ ...f, addressLine1: e.target.value }))} /></label>
          <div className="grid grid-cols-3 gap-3">
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">City *<input className={adminFieldClass()} value={createForm.city} onChange={(e) => setCreateForm((f) => ({ ...f, city: e.target.value }))} /></label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">State *<input className={adminFieldClass()} value={createForm.state} onChange={(e) => setCreateForm((f) => ({ ...f, state: e.target.value }))} /></label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">ZIP *<input className={adminFieldClass()} value={createForm.zip} onChange={(e) => setCreateForm((f) => ({ ...f, zip: e.target.value }))} /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Lat *<input className={adminFieldClass()} value={createForm.locationLat} onChange={(e) => setCreateForm((f) => ({ ...f, locationLat: e.target.value }))} /></label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Lng *<input className={adminFieldClass()} value={createForm.locationLng} onChange={(e) => setCreateForm((f) => ({ ...f, locationLng: e.target.value }))} /></label>
          </div>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Denomination
            <select className={adminFieldClass()} value={createForm.denomination} onChange={(e) => setCreateForm((f) => ({ ...f, denomination: e.target.value }))}>
              {DENOMINATION_OPTIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">Owner user ID <span className="font-normal text-muted">(optional)</span><input className={adminFieldClass()} value={createForm.ownerUserId} onChange={(e) => setCreateForm((f) => ({ ...f, ownerUserId: e.target.value }))} /></label>
          <label className="flex items-center gap-2 text-[13px] cursor-pointer"><input type="checkbox" checked={createForm.claimed} onChange={(e) => setCreateForm((f) => ({ ...f, claimed: e.target.checked }))} />Claimed listing</label>
        </AdminModal>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete church?"
          body={<><strong>{confirmDelete.name}</strong> will be soft-deleted along with its events, denying any pending claim requests. It can be restored from the deleted churches view.</>}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          onConfirm={() => handleLiveDelete(confirmDelete)}
          onCancel={() => { setConfirmDelete(null); deleteChurch.reset() }}
          isPending={deleteChurch.isPending}
          error={deleteChurch.error instanceof Error ? deleteChurch.error.message : null}
        />
      )}

      {archiveDuplicateTarget && (
        <ConfirmModal
          title="Archive this church?"
          body={<><strong>{archiveDuplicateTarget.name}</strong> will be soft-deleted along with its events, denying any pending claim requests. It can be restored from the deleted churches view in the Live tab.</>}
          confirmLabel="Archive"
          pendingLabel="Archiving…"
          onConfirm={() => handleArchiveDuplicate(archiveDuplicateTarget)}
          onCancel={() => { setArchiveDuplicateTarget(null); deleteChurch.reset() }}
          isPending={deleteChurch.isPending}
          error={deleteChurch.error instanceof Error ? deleteChurch.error.message : null}
        />
      )}
    </div>
  )
}
